import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, Switch, message, Space, Tag, Popconfirm, Select, Alert, Spin, Descriptions, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ApiOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import sslService from '../../services/sslService';

const { Option } = Select;
const { TextArea } = Input;

const SslProviders = () => {
  const [providers, setProviders] = useState([]);
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pluginsLoading, setPluginsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [selectedPlugin, setSelectedPlugin] = useState(null);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchProviders();
    fetchPlugins();
  }, []);

  const fetchPlugins = async () => {
    setPluginsLoading(true);
    try {
      const response = await sslService.getPlugins();
      if (response.success) {
        setPlugins(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch plugins:', error);
      // Fallback to default plugins if API fails
      setPlugins([
        {
          name: 'gogetssl',
          display_name: 'GoGetSSL',
          description: 'Commercial SSL certificate reseller. Supports DV, OV, EV certificates from multiple CAs.',
          supported_types: { dv: true, ov: true, ev: true, wildcard: true, multi_domain: true, san: true },
          credential_fields: [
            { name: 'api_username', label: 'API Username', type: 'text', required: true },
            { name: 'api_password', label: 'API Password', type: 'password', required: true },
          ]
        },
        {
          name: 'letsencrypt',
          display_name: "Let's Encrypt (ACME)",
          description: 'Free, automated SSL certificates via ACME protocol. Supports DV certificates with 90-day validity.',
          supported_types: { dv: true, wildcard: true },
          credential_fields: [
            { name: 'eab_kid', label: 'EAB Key ID (Optional)', type: 'text', required: false },
            { name: 'eab_hmac_key', label: 'EAB HMAC Key (Optional)', type: 'password', required: false },
          ]
        },
        {
          name: 'zerossl',
          display_name: 'ZeroSSL',
          description: 'Free and paid SSL certificates. Supports REST API and ACME protocol.',
          supported_types: { dv: true, wildcard: true, multi_domain: true },
          credential_fields: [
            { name: 'api_key', label: 'ZeroSSL API Key', type: 'password', required: true },
            { name: 'eab_kid', label: 'EAB Key ID (Optional)', type: 'text', required: false },
            { name: 'eab_hmac_key', label: 'EAB HMAC Key (Optional)', type: 'password', required: false },
          ]
        },
        {
          name: 'manual',
          display_name: 'Manual Upload',
          description: 'Allows users to manually upload their own SSL certificates from any CA.',
          supported_types: { dv: true, ov: true, ev: true, wildcard: true, multi_domain: true, ip: true, san: true },
          credential_fields: []
        },
      ]);
    } finally {
      setPluginsLoading(false);
    }
  };

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await sslService.getProviders(true); // Admin mode
      if (response.success) {
        setProviders(response.data);
      }
    } catch (error) {
      message.error('Failed to fetch providers');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedPluginInfo = () => {
    if (!selectedPlugin) return null;
    return plugins.find(p => p.name === selectedPlugin);
  };

  const handleAdd = () => {
    setEditingProvider(null);
    setSelectedPlugin(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setSelectedPlugin(provider.provider);

    // Parse api_credentials if it's a JSON string
    let formValues = { ...provider };
    if (provider.api_credentials) {
      try {
        const credentials = typeof provider.api_credentials === 'string'
          ? JSON.parse(provider.api_credentials)
          : provider.api_credentials;
        formValues = { ...formValues, ...credentials };
      } catch (e) {
        console.error('Failed to parse api_credentials:', e);
      }
    }

    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handlePluginChange = (value) => {
    setSelectedPlugin(value);

    // Get the selected plugin info
    const pluginInfo = plugins.find(p => p.name === value);
    if (pluginInfo) {
      // Auto-fill supported types from plugin
      const supportedTypes = pluginInfo.supported_types || {};
      form.setFieldsValue({
        supports_dv: supportedTypes.dv || false,
        supports_ov: supportedTypes.ov || false,
        supports_ev: supportedTypes.ev || false,
        supports_wildcard: supportedTypes.wildcard || false,
        supports_multi_domain: supportedTypes.multi_domain || false,
        supports_ip: supportedTypes.ip || false,
        supports_san: supportedTypes.san || false,
      });

      // Set default API endpoint based on plugin
      if (value === 'letsencrypt') {
        form.setFieldsValue({ api_endpoint: 'https://acme-v02.api.letsencrypt.org/directory' });
      } else if (value === 'zerossl') {
        form.setFieldsValue({ api_endpoint: 'https://api.zerossl.com' });
      } else if (value === 'gogetssl') {
        form.setFieldsValue({ api_endpoint: 'https://my.gogetssl.com/api' });
      }
    }

    // Clear API-related fields when changing plugin
    form.setFieldsValue({
      api_key: undefined,
      api_username: undefined,
      api_password: undefined,
      eab_kid: undefined,
      eab_hmac_key: undefined,
    });
  };

  const handleGenerateEab = async () => {
    const apiKey = form.getFieldValue('api_key');
    if (!apiKey) {
      message.warning('请先填写 ZeroSSL API Key');
      return;
    }
    try {
      const response = await sslService.generateEab(apiKey);
      if (response.success && response.data) {
        form.setFieldsValue({
          eab_kid: response.data.eab_kid,
          eab_hmac_key: response.data.eab_hmac_key,
        });
        message.success('EAB 凭据已生成并填入表单，请保存 Provider 以持久化。');
      } else {
        message.error(response.error || response.message || '生成 EAB 失败');
      }
    } catch (error) {
      message.error('生成 EAB 失败：' + (error.error || error.message || '未知错误'));
    }
  };

  const handleDelete = async (id) => {
    try {
      await sslService.deleteProvider(id);
      message.success('Provider deleted successfully');
      fetchProviders();
    } catch (error) {
      message.error('Failed to delete provider');
    }
  };

  const handleTestConnection = async (provider) => {
    setTestingProvider(provider);
    setTestResult(null);
    setTestModalVisible(true);
    setTestLoading(true);

    try {
      const response = await sslService.testProvider(provider.id);
      setTestResult(response);
    } catch (error) {
      setTestResult({
        success: false,
        message: error.message || error.error || 'Connection test failed'
      });
    } finally {
      setTestLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const pluginInfo = getSelectedPluginInfo();

      // Build api_credentials based on plugin's credential_fields
      let api_credentials = null;
      if (pluginInfo && pluginInfo.credential_fields && pluginInfo.credential_fields.length > 0) {
        api_credentials = {};
        pluginInfo.credential_fields.forEach(field => {
          if (values[field.name]) {
            api_credentials[field.name] = values[field.name];
          }
        });
        // Only set if we have at least one credential
        if (Object.keys(api_credentials).length === 0) {
          api_credentials = null;
        }
      }

      const data = {
        provider: values.provider,
        name: values.name,
        description: values.description || null,
        api_endpoint: values.api_endpoint || null,
        api_credentials: api_credentials ? JSON.stringify(api_credentials) : null,
        supports_dv: values.supports_dv || false,
        supports_ov: values.supports_ov || false,
        supports_ev: values.supports_ev || false,
        supports_wildcard: values.supports_wildcard || false,
        supports_multi_domain: values.supports_multi_domain || false,
        supports_ip: values.supports_ip || false,
        supports_san: values.supports_san || false,
        is_active: values.is_active || false,
        is_default: values.is_default || false,
        hide_from_shop: values.hide_from_shop || false,
        priority: values.priority || 0,
      };

      if (editingProvider) {
        await sslService.updateProvider(editingProvider.id, data);
        message.success('Provider updated successfully');
      } else {
        await sslService.createProvider(data);
        message.success('Provider created successfully');
      }
      setModalVisible(false);
      fetchProviders();
    } catch (error) {
      message.error('Failed to save provider: ' + (error.message || error.error || 'Unknown error'));
      console.error('Failed to save provider:', error);
    }
  };

  // Render credential fields dynamically based on selected plugin
  const renderCredentialFields = () => {
    const pluginInfo = getSelectedPluginInfo();
    if (!pluginInfo) return null;

    // Manual plugin has no credential fields
    if (pluginInfo.name === 'manual') {
      return (
        <Alert
          message="Manual Upload Provider"
          description="This provider allows users to manually upload their SSL certificates. No API configuration is required."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      );
    }

    // API Endpoint (common for most plugins)
    const showApiEndpoint = ['letsencrypt', 'zerossl', 'gogetssl', 'acme'].includes(pluginInfo.name);

    const credentialFields = pluginInfo.credential_fields || [];

    return (
      <>
        {showApiEndpoint && (
          <Form.Item
            name="api_endpoint"
            label="API Endpoint"
          >
            <Input placeholder={`Enter ${pluginInfo.display_name} API endpoint`} />
          </Form.Item>
        )}

        {credentialFields.map(field => (
          <Form.Item
            key={field.name}
            name={field.name}
            label={field.label}
            rules={field.required ? [{ required: true, message: `Please enter ${field.label}` }] : []}
            extra={field.description}
          >
            {field.type === 'password' ? (
              <Input.Password placeholder={`Enter ${field.label}`} />
            ) : (
              <Input placeholder={`Enter ${field.label}`} />
            )}
          </Form.Item>
        ))}

        {/* ZeroSSL can auto-generate ACME EAB credentials from the API key */}
        {pluginInfo.name === 'zerossl' && (
          <Form.Item>
            <Button icon={<ApiOutlined />} onClick={handleGenerateEab}>
              从 API Key 自动生成 EAB 凭据
            </Button>
          </Form.Item>
        )}
      </>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Plugin',
      dataIndex: 'provider',
      key: 'provider',
      render: (text, record) => {
        const pluginInfo = plugins.find(p => p.name === text);
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{pluginInfo?.display_name || text}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.name}</div>
          </div>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Features',
      key: 'features',
      render: (_, record) => (
        <Space wrap>
          {record.supports_dv && <Tag color="blue">DV</Tag>}
          {record.supports_ov && <Tag color="green">OV</Tag>}
          {record.supports_ev && <Tag color="gold">EV</Tag>}
          {record.supports_wildcard && <Tag color="purple">Wildcard</Tag>}
          {record.supports_multi_domain && <Tag color="cyan">Multi-Domain</Tag>}
          {record.supports_ip && <Tag color="orange">IP</Tag>}
          {record.supports_san && <Tag color="magenta">SAN</Tag>}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.is_active ? 'green' : 'red'}>
            {record.is_active ? 'Active' : 'Inactive'}
          </Tag>
          {record.hide_from_shop && <Tag color="orange">Hidden from Shop</Tag>}
        </Space>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<ApiOutlined />}
            onClick={() => handleTestConnection(record)}
          >
            Test
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          {record.id > 3 && ( // Don't allow deleting fixed providers (1,2,3)
            <Popconfirm
              title="删除 Provider"
              description="删除后关联的产品可能无法签发证书，确定删除吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="证书供应商"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Provider
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={providers}
          loading={loading}
          rowKey="id"
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无 Provider，点击右上角添加 Provider" /> }}
        />
      </Card>

      <Modal
        title={editingProvider ? 'Edit Provider' : 'Add Provider'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Spin spinning={pluginsLoading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="provider"
              label="Plugin"
              rules={[{ required: true, message: 'Please select a plugin' }]}
            >
              <Select
                placeholder="Select plugin"
                onChange={handlePluginChange}
                disabled={!!editingProvider}
              >
                {plugins.map(plugin => (
                  <Option key={plugin.name} value={plugin.name}>
                    {plugin.display_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {selectedPlugin && (
              <Alert
                message={`Plugin: ${getSelectedPluginInfo()?.display_name || selectedPlugin}`}
                description={getSelectedPluginInfo()?.description}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Form.Item
              name="name"
              label="Display Name"
              rules={[{ required: true, message: 'Please enter display name' }]}
            >
              <Input placeholder="e.g., My SSL Provider" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea rows={2} placeholder="Brief description of this provider" />
            </Form.Item>

            {/* Dynamic credential fields based on plugin */}
            {renderCredentialFields()}

            <Form.Item label="Supported Features">
              <Space direction="vertical">
                <Form.Item name="supports_dv" valuePropName="checked" noStyle>
                  <Switch /> <span style={{ marginLeft: 8 }}>Domain Validation (DV)</span>
                </Form.Item>
                <Form.Item name="supports_ov" valuePropName="checked" noStyle>
                  <Switch /> <span style={{ marginLeft: 8 }}>Organization Validation (OV)</span>
                </Form.Item>
                <Form.Item name="supports_ev" valuePropName="checked" noStyle>
                  <Switch /> <span style={{ marginLeft: 8 }}>Extended Validation (EV)</span>
                </Form.Item>
                <Form.Item name="supports_wildcard" valuePropName="checked" noStyle>
                  <Switch /> <span style={{ marginLeft: 8 }}>Wildcard Certificates</span>
                </Form.Item>
                <Form.Item name="supports_multi_domain" valuePropName="checked" noStyle>
                  <Switch /> <span style={{ marginLeft: 8 }}>Multi-Domain</span>
                </Form.Item>
                <Form.Item name="supports_ip" valuePropName="checked" noStyle>
                  <Switch /> <span style={{ marginLeft: 8 }}>IP Certificates</span>
                </Form.Item>
                <Form.Item name="supports_san" valuePropName="checked" noStyle>
                  <Switch /> <span style={{ marginLeft: 8 }}>SAN (Subject Alternative Names)</span>
                </Form.Item>
              </Space>
            </Form.Item>

            <Form.Item
              name="priority"
              label="Priority"
              initialValue={0}
            >
              <Input type="number" />
            </Form.Item>

            <Form.Item
              name="is_active"
              label="Active"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="is_default"
              label="Set as Default"
              valuePropName="checked"
              initialValue={false}
              extra="Default provider will be pre-selected for new SSL orders"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="hide_from_shop"
              label="Hide from Shop"
              valuePropName="checked"
              initialValue={false}
              extra="Hidden providers won't show in user-facing SSL certificate shop"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Test Connection Modal */}
      <Modal
        title={`Test Connection: ${testingProvider?.name || ''}`}
        open={testModalVisible}
        onCancel={() => {
          setTestModalVisible(false);
          setTestResult(null);
        }}
        footer={[
          <Button key="close" onClick={() => setTestModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="retry"
            type="primary"
            onClick={() => handleTestConnection(testingProvider)}
            loading={testLoading}
          >
            Retry
          </Button>
        ]}
        width={500}
      >
        {testLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Testing connection...</p>
          </div>
        ) : testResult ? (
          <div>
            <Alert
              type={testResult.success ? 'success' : 'error'}
              message={testResult.success ? 'Connection Successful' : 'Connection Failed'}
              description={testResult.message}
              icon={testResult.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              showIcon
              style={{ marginBottom: 16 }}
            />
            {testResult.success && testResult.data && (
              <Descriptions bordered column={1} size="small">
                {Object.entries(testResult.data).map(([key, value]) => (
                  <Descriptions.Item key={key} label={key.replace(/_/g, ' ').toUpperCase()}>
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default SslProviders;
